import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useUserPreferences as useUserPreferencesHook, UserPreferences } from '@/hooks/useUserPreferences';
import { useTheme } from 'next-themes';

interface UserPreferencesContextType {
  preferences: UserPreferences | null;
  loading: boolean;
  updating: boolean;
  updatePreferences: (updates: Partial<UserPreferences>) => Promise<void>;
  refetch: () => Promise<void>;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | null>(null);

export const UserPreferencesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const value = useUserPreferencesHook();
  const { setTheme } = useTheme();

  // Sync theme from DB on login (cross-device persistence)
  useEffect(() => {
    if (value.preferences?.theme_preference) {
      setTheme(value.preferences.theme_preference);
    }
  }, [value.preferences?.theme_preference, setTheme]);

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useSharedUserPreferences = () => {
  const ctx = useContext(UserPreferencesContext);
  if (!ctx) {
    // Fallback: if used outside provider, create own instance
    // This shouldn't happen in practice but prevents crashes
    return useUserPreferencesHook();
  }
  return ctx;
};
